// All 28 questions organized by phase
export const questions = {
  phase1: {
    title: "Phase 1: Brain Pattern Analysis",
    subtitle: "Understanding how you naturally capture and organize information",
    questions: [
      {
        id: 1,
        question: "How do you currently capture new tasks or ideas when they pop up?",
        type: "single",
        options: [
          "I write them immediately in my planner",
          "I use sticky notes and transfer later",
          "I use my phone (notes app, reminders)",
          "I try to remember them (and often forget)",
          "I say them into voice memos",
          "I email/text myself",
          "Other"
        ]
      },
      {
        id: 2,
        question: "When you look at a blank planner page, how do you feel?",
        type: "single",
        options: [
          "Excited - I love the freedom to create my own layout",
          "Overwhelmed - I need structure to know where to start",
          "Frustrated - I wish it already had sections printed",
          "Indifferent - I just need it to work"
        ]
      },
      {
        id: 3,
        question: "Which statement resonates most with you?",
        type: "single",
        options: [
          "I love creating spreads from scratch - it's therapeutic",
          "I prefer printed templates I can just fill in",
          "I like a mix - some structure with room for creativity",
          "I just want the fastest way to get things done"
        ]
      },
      {
        id: 4,
        question: "Think about the last planner you abandoned. What made you stop using it?",
        type: "single",
        options: [
          "Too complicated - too many sections I didn't need",
          "Too simple - didn't have enough structure",
          "Too time-consuming to maintain",
          "The layout didn't match how my brain works",
          "I felt guilty when I missed days",
          "It was beautiful but not functional",
          "Other"
        ]
      },
      {
        id: 5,
        question: "Which planning activities do you actually ENJOY doing? (Select all that apply)",
        type: "multiple",
        options: [
          "Writing out my day hour by hour",
          "Brain dumping all my tasks onto paper",
          "Color-coding and highlighting",
          "Checking off completed tasks",
          "Monthly planning and goal-setting",
          "Weekly reviews",
          "Decorating/making it pretty",
          "Nothing - I just want it done fast",
          "Other"
        ]
      },
      {
        id: 6,
        question: "Which planning activities do you absolutely HATE or avoid? (Select all that apply)",
        type: "multiple",
        options: [
          "Habit tracking (feels like failure reminder)",
          "Time blocking (too rigid)",
          "Daily journaling/reflection",
          "Migrating uncompleted tasks",
          "Creating layouts from scratch",
          "Detailed planning (prefer flexibility)",
          "Morning/evening routines tracking",
          "Meal planning",
          "Budget tracking",
          "Other"
        ]
      },
      {
        id: 7,
        question: "When do you prefer to plan?",
        type: "single",
        options: [
          "Sunday evening for the week ahead",
          "Every morning for that day",
          "Monthly, then adjust as I go",
          "I prefer NOT to pre-plan, I plan as things come",
          "Whenever I remember to",
          "Other"
        ]
      },
      {
        id: 8,
        question: "How detailed do you like your plans?",
        type: "single",
        options: [
          "Very detailed - I want every hour accounted for",
          "Moderately detailed - main tasks with some time frames",
          "Loose structure - just a list of what needs doing",
          "Minimal - just the absolute must-dos"
        ]
      }
    ]
  },
  phase2: {
    title: "Phase 2: Life Flow Reality Check",
    subtitle: "Understanding your roles, priorities, and time reality",
    questions: [
      {
        id: 9,
        question: "Which roles do you currently play in your life? (Select all that apply)",
        type: "multiple",
        options: [
          "Parent/Caregiver",
          "Spouse/Partner",
          "Professional/Employee",
          "Business Owner/Entrepreneur",
          "Student",
          "Caregiver (for elderly parents or family members)",
          "Volunteer/Community member",
          "Homemaker/Household manager",
          "Creative/Artist",
          "Self-care/Personal development",
          "Other"
        ]
      },
      {
        id: 10,
        question: "How many of these roles can you REALISTICALLY give focused attention to at the same time?",
        type: "single",
        options: [
          "1-2 roles (I need deep focus)",
          "3-4 roles (balanced approach)",
          "5-6 roles (I can juggle a lot)",
          "7+ roles (I try to balance everything)",
          "It varies by season/month"
        ]
      },
      {
        id: 11,
        question: "Select your TOP 3 PRIORITY roles for this current season of life:",
        type: "ranking",
        dependsOn: 9,
        instruction: "Rank your top 3 priorities from the roles you selected above"
      },
      {
        id: 12,
        question: "Do your role priorities shift throughout the year?",
        type: "single",
        options: [
          "Yes, they change based on what's happening (work seasons, school schedules, projects)",
          "Somewhat - minor shifts but mostly consistent",
          "No, my priorities stay pretty much the same",
          "I've never thought about this before"
        ]
      },
      {
        id: 13,
        question: "If yes to previous question, what triggers your priority shifts? (Select all that apply)",
        type: "multiple",
        conditional: true,
        showIf: (answers) => {
          const q12Answer = answers[12];
          return q12Answer && q12Answer.includes("Yes, they change");
        },
        options: [
          "School/exam seasons",
          "Work deadlines or busy seasons",
          "Family events or needs",
          "Personal goals or projects",
          "Health situations",
          "Financial pressures",
          "Holidays or travel",
          "Other"
        ]
      },
      {
        id: 14,
        question: "How many hours per day do you realistically have for focused work/tasks?",
        type: "single",
        options: [
          "Less than 2 hours (very fragmented time)",
          "2-4 hours",
          "4-6 hours",
          "6-8 hours",
          "8+ hours",
          "It varies wildly day to day"
        ]
      },
      {
        id: 15,
        question: "When are you most productive?",
        type: "single",
        options: [
          "Early morning (5am-9am)",
          "Mid-morning (9am-12pm)",
          "Afternoon (12pm-5pm)",
          "Evening (5pm-9pm)",
          "Night (9pm+)",
          "I don't have a consistent pattern"
        ]
      },
      {
        id: 16,
        question: "Which statement best describes your schedule?",
        type: "single",
        options: [
          "Very structured - same routine most days",
          "Moderately structured - some consistent elements",
          "Flexible - every day is different",
          "Chaotic - I never know what's coming",
          "Seasonally changes - structured some months, chaotic others"
        ]
      },
      {
        id: 17,
        question: "What percentage of your day is typically controlled by OTHER people's needs/schedules?",
        type: "single",
        options: [
          "0-25% (I control most of my time)",
          "25-50% (Balanced)",
          "50-75% (Others control most of my time)",
          "75-100% (I'm constantly responding to others)"
        ]
      },
      {
        id: 18,
        question: "How far ahead can you realistically predict your schedule?",
        type: "single",
        options: [
          "I know my whole year",
          "I can predict 3-6 months out",
          "I can plan about a month ahead",
          "I can barely plan a week ahead",
          "I can only plan day-by-day"
        ]
      }
    ]
  },
  phase3: {
    title: "Phase 3: System Architecture",
    subtitle: "Understanding your planning preferences and needs",
    questions: [
      {
        id: 19,
        question: "Which time frames do you NEED to plan for? (Select all that apply)",
        type: "multiple",
        options: [
          "Yearly goals and big picture",
          "Quarterly goals/projects",
          "Monthly overview",
          "Weekly planning",
          "Daily task lists",
          "Hourly time blocking"
        ]
      },
      {
        id: 20,
        question: "Which planning horizon feels most natural to you?",
        type: "single",
        options: [
          "I think in years - big picture goals",
          "I think in quarters - 3-month chunks",
          "I think in months - what needs to happen this month",
          "I think in weeks - weekly rhythm",
          "I think in days - just today's priorities",
          "I think in hours - time-blocking my day"
        ]
      },
      {
        id: 21,
        question: "How do you prefer to break down big projects?",
        type: "single",
        options: [
          "Start with end goal, work backwards",
          "Break into phases/milestones",
          "List all tasks then organize them",
          "Wing it and figure it out as I go",
          "I struggle with breaking down projects"
        ]
      },
      {
        id: 22,
        question: "How often do you have appointments, meetings, or time-specific commitments?",
        type: "single",
        options: [
          "Multiple per day",
          "Several per week",
          "A few per month",
          "Rarely - my schedule is flexible",
          "It varies seasonally"
        ]
      },
      {
        id: 23,
        question: "How often do you work on long-term projects (things taking weeks/months)?",
        type: "single",
        options: [
          "Always - I always have multiple projects going",
          "Often - I usually have 1-2 ongoing projects",
          "Sometimes - occasionally I'll have a big project",
          "Rarely - most of my work is day-to-day tasks",
          "Never - I don't work on extended projects"
        ]
      },
      {
        id: 24,
        question: "How do you feel about seeing ALL your tasks written out?",
        type: "single",
        options: [
          "Motivated - I need to see everything to feel in control",
          "Overwhelmed - too much at once stresses me out",
          "It depends - sometimes helpful, sometimes overwhelming"
        ]
      },
      {
        id: 25,
        question: "When planning your week, what matters most?",
        type: "single",
        options: [
          "Knowing exactly what time I'll do each task",
          "Having a list of what needs to happen",
          "Seeing how busy each day is",
          "Balancing different areas of life",
          "Just remembering appointments and deadlines"
        ]
      },
      {
        id: 26,
        question: "How many different \"life areas\" do you need to track separately?",
        type: "single",
        options: [
          "2 areas (e.g., work & personal)",
          "3-4 areas (work, family, personal, home)",
          "5+ areas (I wear many hats)",
          "I prefer NOT to separate - everything in one place"
        ]
      },
      {
        id: 27,
        question: "What's your biggest planning frustration right now?",
        type: "text",
        placeholder: "Describe in 2-3 sentences..."
      },
      {
        id: 28,
        question: "If your planning system could do ONE thing perfectly, what would it be?",
        type: "text",
        placeholder: "Describe in 1-2 sentences..."
      }
    ]
  }
};

// Lead capture form fields
export const leadCaptureFields = [
  {
    id: "firstName",
    label: "First Name",
    type: "text",
    required: true,
    placeholder: "Enter your first name"
  },
  {
    id: "lastName",
    label: "Last Name",
    type: "text",
    required: true,
    placeholder: "Enter your last name"
  },
  {
    id: "email",
    label: "Email Address",
    type: "email",
    required: true,
    placeholder: "your.email@example.com"
  },
  {
    id: "workStatus",
    label: "Current Work Status",
    type: "select",
    required: true,
    options: [
      "Working full-time",
      "Working part-time",
      "Self-employed/Business owner",
      "Stay-at-home parent",
      "Student",
      "Retired",
      "Other"
    ]
  },
  {
    id: "children",
    label: "Number of Children",
    type: "select",
    required: true,
    options: ["0", "1", "2", "3", "4", "5+"]
  },
  {
    id: "frustration",
    label: "What's your biggest planning frustration right now?",
    type: "textarea",
    required: true,
    placeholder: "Describe in 2-3 sentences..."
  }
];
